import { useMemo } from 'react';

import { Divider, Icon, Pressable, Text } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { ActionsheetWrapper } from 'components/action-sheet';
import { InnerLayout, LayoutContainer } from 'components/layout/styled';
import SignUpHeadline from 'pages/authentication/components/sign-up-headline/sign-up-headline';
import ConfirmOtp from 'pages/authentication/confirm-otp';
import { SignInForm } from 'pages/authentication/sign-in/sign-in-form';
import SignUp from 'pages/authentication/sign-up';
import { useAuthContext } from 'state/auth';
import { ModalAuthScreen } from 'state/auth/types';
import { theme } from 'styles/configure-theme';
import { isWeb } from 'utils/environment';

import { BackButton, ModalAuthActionSheetContent } from './components';

type ModalAuthProps = {
  disableEmailInput?: boolean;
  heading?: string;
  onClose?: () => void;
  onPreSignIn?: () => void | Promise<void>;
  onSignInSuccess?: () => void | Promise<void>;
  shouldDisplaySignInSuccessMessage?: boolean;
  shouldWaitLoginForVendor?: boolean;
  shouldDisableHeadlineLinks?: boolean;
};

export const ModalAuth = ({
  heading,
  disableEmailInput,
  onClose,
  onPreSignIn,
  onSignInSuccess,
  shouldDisplaySignInSuccessMessage,
  shouldWaitLoginForVendor = false,
  shouldDisableHeadlineLinks = false,
}: ModalAuthProps) => {
  const { modalAuthState, modalAuthIsOpen, setModalAuthState } = useAuthContext();
  const { formatMessage } = useIntl();
  const getModalAuthComponent = () =>
    ModalAuthComponents[modalAuthState.screen || ModalAuthScreen.CLOSED];

  const handleOnClose = () => {
    if (onClose) {
      onClose();
    } else {
      setModalAuthState({ screen: ModalAuthScreen.CLOSED });
    }
  };

  const ModalAuthComponents = useMemo(
    () => ({
      [ModalAuthScreen.SIGN_IN]: (
        <SignInForm
          disableEmailInput={disableEmailInput}
          onSuccess={onSignInSuccess}
          shouldDisplaySignInSuccessMessage={shouldDisplaySignInSuccessMessage}
          onPreSignIn={onPreSignIn}
        />
      ),
      [ModalAuthScreen.SIGN_UP]: (
        <SignUp skipRedirect shouldWaitLoginForVendor={shouldWaitLoginForVendor} />
      ),
      [ModalAuthScreen.CHALLENGE]: <ConfirmOtp onSuccess={onSignInSuccess} />,
      [ModalAuthScreen.CLOSED]: null,
    }),
    [
      onPreSignIn,
      onSignInSuccess,
      shouldDisplaySignInSuccessMessage,
      shouldWaitLoginForVendor,
      disableEmailInput,
    ]
  );

  if (!modalAuthIsOpen) {
    return null;
  }

  return (
    <ActionsheetWrapper
      isOpen
      onOpenEventMessage="Open modal authentication"
      onClose={handleOnClose}
    >
      <ModalAuthActionSheetContent>
        {isWeb && (
          <Pressable width="$6" marginLeft="$4" onPress={handleOnClose}>
            <Icon
              variant="cancel"
              color={theme.token('icon-default')}
              title={formatMessage({ id: 'back' })}
            />
          </Pressable>
        )}
        <BackButton />
        <LayoutContainer isModal width="full">
          <InnerLayout>
            <SignUpHeadline disableLink={shouldDisableHeadlineLinks} />
            <Divider background="blackOpacity.30" marginTop="$6" marginBottom="$6" />
            {heading && <Text marginBottom="$6">{heading}</Text>}
            {getModalAuthComponent()}
          </InnerLayout>
        </LayoutContainer>
      </ModalAuthActionSheetContent>
    </ActionsheetWrapper>
  );
};
